import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SectionHeadingProps {
    eyebrow?: string
    title: ReactNode
    description?: ReactNode
    action?: ReactNode
    align?: 'start' | 'center'
    as?: 'h1' | 'h2'
    tone?: 'default' | 'inverted'
    titleId?: string
    className?: string
}

export const SectionHeading: FC<SectionHeadingProps> = ({
    eyebrow,
    title,
    description,
    action,
    align = 'start',
    as: Heading = 'h2',
    tone = 'default',
    titleId,
    className
}): ReactNode => {
    const isCentered = align === 'center'
    const isInverted = tone === 'inverted'

    return (
        <div
            className={cn(
                'flex flex-col gap-6',
                isCentered ? 'items-center text-center' : 'md:flex-row md:items-end md:justify-between',
                className
            )}
        >
            <div className={cn('max-w-2xl', isCentered && 'flex flex-col items-center')}>
                {eyebrow && (
                    <p className={cn('mb-4 flex items-center gap-3 text-sm font-medium', isInverted ? 'text-accent' : 'text-brand')}>
                        <span className={cn('h-px w-8', isInverted ? 'bg-accent/60' : 'bg-brand/60')} aria-hidden='true' />
                        {eyebrow}
                    </p>
                )}

                <Heading
                    id={titleId}
                    className={cn(
                        'font-heading font-semibold leading-[1.35] text-balance',
                        isInverted ? 'text-primary-foreground' : 'text-foreground',
                        Heading === 'h1' ? 'text-4xl sm:text-5xl lg:text-6xl' : 'text-3xl sm:text-4xl lg:text-5xl'
                    )}
                >
                    {title}
                </Heading>

                {description && (
                    <div className={cn('mt-4 text-base leading-8 sm:text-lg sm:leading-9', isInverted ? 'text-primary-foreground/75' : 'text-muted-foreground')}>
                        {description}
                    </div>
                )}
            </div>

            {action && (
                <div className='shrink-0'>
                    {action}
                </div>
            )}
        </div>
    )
}
